import { useForm } from "react-hook-form";
import apiService from "services/api";
import { Button } from "reactstrap";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import randomId from "utils/random";
import { useQuery } from "react-query";
import remarkGfm from "remark-gfm";

const defaultValues = {
	question: "",
	action: "chat",
};

const ChatGPTAnswer = ({ prompt }) => (
	<div className="rounded text-left bg-dark mr-5 py-2 px-3">
		<small>
			<small className="text-muted d-inline-block mb-2">Answer by ChatGPT</small>
			{prompt.message.length ? <ReactMarkdown children={prompt.message} remarkPlugins={[remarkGfm]} /> : <div>Loading...</div>}
		</small>
	</div>
);

const ChatGPTQuestion = ({ prompt }) => (
	<div className="bg-info d-inline-block rounded text-right px-3 pb-2">
		<small>
			<small className="text-light d-inline-block mb-1">Me</small>
			<div className="text-white">{prompt.message}</div>
		</small>
	</div>
);

const useChatGPTPrompt = () => {
	const [prompts, setPrompts] = useState([]);

	const ChatBox = () =>
		prompts.map((prompt) => (
			<div key={prompt.id} className={`${prompt.type === "question" && "d-flex justify-content-end"} m-1 mb-4 rounded`}>
				{prompt.type === "answer" ? <ChatGPTAnswer prompt={prompt} /> : <ChatGPTQuestion prompt={prompt} />}
			</div>
		));
	return { setPrompts, ChatBox };
};

const ChatGPTPage = () => {
	const { data: authToken } = useQuery(["authTokenRefresh"], apiService.auth.refresh, { refetchOnWindowFocus: true });
	const webSocket = useRef(null);
	const [disabled, setDisabled] = useState(false);
	const markdownContainer = useRef(null);

	const { setPrompts, ChatBox } = useChatGPTPrompt();

	const { register, handleSubmit, reset } = useForm({
		defaultValues,
	});

	const sendQuestion = useCallback(
		(data) => {
			const socketUrl = `wss://inb1ylwu35.execute-api.ap-southeast-1.amazonaws.com/production?token=${authToken}`;
			if (!data.question) return;
			setDisabled(true);
			const newWebSocket = new WebSocket(socketUrl);
			const newAnswerId = randomId();
			reset(defaultValues);
			setPrompts((prevPrompts) => [...prevPrompts, { id: randomId(), message: data.question, type: "question" }]);
			setPrompts((prevPrompts) => [...prevPrompts, { id: newAnswerId, message: "", type: "answer" }]);
			markdownContainer.current.scrollTop = markdownContainer.current.scrollHeight;
			newWebSocket.onopen = () => {
				newWebSocket.send(JSON.stringify(data));
			};
			newWebSocket.onmessage = (message) => {
				setPrompts((prevPrompts) => {
					return prevPrompts.map((_prompt) =>
						_prompt.id === newAnswerId ? { ..._prompt, message: _prompt.message + message.data } : _prompt
					);
				});
				markdownContainer.current.scrollTop = markdownContainer.current.scrollHeight;
			};
			newWebSocket.onclose = () => setDisabled(false);
			webSocket.current = newWebSocket;
		},
		[authToken]
	);

	useEffect(() => {
		if (webSocket.current !== null) return () => webSocket.current.close();
	}, []);

	return (
		<div className="content d-flex" style={{ flexDirection: "column", height: "98vh" }}>
			<div className="d-flex card card-body mb-0 p-0">
				<div style={{ flexGrow: 1, maxHeight: "100%", overflowY: "auto" }} ref={markdownContainer} className="rounded px-3 py-2">
					<ChatBox />
				</div>
				<form onSubmit={handleSubmit(sendQuestion)} className="d-flex p-1 border-top border-dark align-items-center">
					<input
						{...register("question", { required: true })}
						style={{ flexGrow: 1, outline: "none" }}
						className="bg-light border-0 text-light px-2 mx-1 py-1 rounded text-dark"
						placeholder="Ask a question"
					/>
					<Button color="success" type="submit" size="sm" disabled={disabled || !authToken}>
						Send
					</Button>
				</form>
			</div>
		</div>
	);
};

export default ChatGPTPage;
